import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import useTranslation from '../hooks/useTranslation';
import { getReadableErrorMessage } from '../utils/errorHandler';

// Configure axios to send cookies with every request
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

// =============================================================================
// OPARA v7.0 - SACRED DOMAIN DETECTION (Frontend)
// =============================================================================
// KALICI ÇÖZÜM: Production domain'de çalışırken otomatik olarak doğru URL'yi kullan
// Bu sayede fork/deployment sonrası manuel ayar gerekmez
// =============================================================================
const PRODUCTION_DOMAIN = 'opara.co';

function getApiUrl() {
  // KURAL 1: Eğer production domain'deyse, her zaman production URL kullan
  if (typeof window !== 'undefined' && window.location.hostname === PRODUCTION_DOMAIN) {
    console.log('[API URL] 🔒 SACRED DOMAIN DETECTED! Using production URL');
    return `https://${PRODUCTION_DOMAIN}`;
  }
  
  // KURAL 2: Diğer durumlarda environment variable kullan
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (envUrl) {
    console.log('[API URL] Using env variable:', envUrl);
    return envUrl;
  }
  
  // KURAL 3: Fallback - localhost
  console.log('[API URL] Fallback to localhost');
  return 'http://localhost:8001';
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  // SACRED DOMAIN DETECTION - Runtime'da doğru URL belirlenir
  const API_URL = getApiUrl();

  // Load token from localStorage on mount (backwards compatible)
  // Also check if HttpOnly cookie session is valid
  useEffect(() => {
    console.log('[AUTH] Checking for existing session...');
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('user');
    
    console.log('[AUTH] Found localStorage token:', storedToken ? 'YES' : 'NO');
    console.log('[AUTH] Found localStorage user:', storedUser ? 'YES' : 'NO');
    
    if (storedToken && storedUser) {
      // PERFORMANCE FIX: Set user immediately from cache, then verify in background
      const parsedUser = JSON.parse(storedUser);
      setToken(storedToken);
      setUser(parsedUser);
      setLoading(false); // Immediately show UI
      
      // Verify token in background (don't block UI)
      fetchUserData(storedToken);
    } else {
      // Try to verify session with HttpOnly cookie
      verifySession();
    }
  }, []);

  // Verify session using HttpOnly cookie (no token needed)
  const verifySession = async () => {
    try {
      console.log('[AUTH] Verifying session with HttpOnly cookie...');
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true  // Important: send cookies
      });
      
      // Cookie session is valid
      console.log('[AUTH] HttpOnly cookie session valid');
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      setLoading(false);
    } catch (error) {
      console.log('[AUTH] No valid session, user needs to login');
      setLoading(false);
    }
  };

  const fetchUserData = async (authToken) => {
    try {
      // STALE-WHILE-REVALIDATE: cache zaten mount'ta gosterildi (anlik UI, flicker yok);
      //   burada HER ZAMAN /auth/me cagrilir -> kredi bayat kalmaz (5-dk TTL kredi'yi geciktiriyordu).
      const now = Date.now();

      // Fetch fresh data - support both header and cookie
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {},
        withCredentials: true
      });

      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      localStorage.setItem('user_cache_timestamp', now.toString());
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      // Token invalid, logout
      logout();
      setLoading(false);
    }
  };

  const register = async (email, password, firstName, lastName, additionalInfo = "", marketingOptIn = false, locale = 'tr') => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        additional_info: additionalInfo,
        marketing_opt_in: marketingOptIn,
        locale
      }, {
        withCredentials: true
      });

      // New backend response format with email verification
      if (response.data.requires_verification) {
        // Store email for verification page
        localStorage.setItem('pending_verification_email', email);
        return { 
          success: true, 
          requiresVerification: true,
          email: email,
          message: response.data.message
        };
      }
      
      // Staff registration (no verification needed)
      if (response.data.is_staff) {
        return {
          success: true,
          requiresVerification: false,
          isStaff: true,
          message: response.data.message
        };
      }
      
      // Legacy flow (if backend returns token directly - for Google OAuth completion)
      const { access_token, user: userData } = response.data;
      
      if (access_token) {
        setToken(access_token);
        setUser(userData);
        
        // Store in localStorage (backwards compatible)
        localStorage.setItem('auth_token', access_token);
        localStorage.setItem('user', JSON.stringify(userData));
      }
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: getReadableErrorMessage(error, t, 'notifications.register_failed'),
        code: error.response?.data?.code  // C3a-3: kod-tabanli ozel-durum kontrolu (locale-bagimsiz)
      };
    }
  };

  const login = async (email, password, totpCode = null) => {
    try {
      console.log('[AUTH] Login attempt for:', email);
      const loginData = { email, password };
      
      // Add TOTP code if provided (for 2FA)
      if (totpCode) {
        loginData.totp_code = totpCode;
      }
      
      const response = await axios.post(`${API_URL}/api/auth/login`, loginData, {
        withCredentials: true  // Important: receive and store cookies
      });

      console.log('[AUTH] Login response:', response.data);
      
      // Check if 2FA is required - MUST be before destructuring access_token
      if (response.data.requires_2fa) {
        return {
          success: false,
          requires2FA: true,
          tempToken: response.data.temp_token,
          qrCode: response.data.qr_code,
          isFirstSetup: response.data.is_first_setup,
          message: response.data.message
        };
      }
      
      const { access_token, user: userData } = response.data;
      
      if (!access_token) {
        console.error('[AUTH] No access_token in response!');
        return { success: false, error: t('notifications.token_missing') };
      }
      
      setToken(access_token);
      setUser(userData);
      
      // Store in localStorage (backwards compatible, but HttpOnly cookie is primary)
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Debug: Verify storage
      console.log('[AUTH] Token saved. HttpOnly cookie also set by server.');
      
      return { success: true };
    } catch (error) {
      console.error('[AUTH] Login error:', error);
      return {
        success: false,
        error: getReadableErrorMessage(error, t, 'notifications.login_failed')
      };
    }
  };

  const logout = async () => {
    try {
      // Call backend to clear HttpOnly cookie
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('[AUTH] Logout API error:', error);
    }
    
    // Clear local state
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_cache_timestamp');
  };

  const refreshUserData = async () => {
    // FORCE REFRESH: Clear cache to get fresh data from server
    // This is critical after payment completion
    localStorage.removeItem('user_cache_timestamp');
    
    if (token) {
      await fetchUserData(token);
    } else {
      // Try with cookie only
      await verifySession();
    }
  };

  const loginWithToken = async (authToken) => {
    console.log('[loginWithToken] Starting with token:', authToken?.substring(0, 20) + '...');
    try {
      // Fetch user data with the provided token
      console.log('[loginWithToken] Calling /api/auth/me...');
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        withCredentials: true
      });
      
      console.log('[loginWithToken] Response received:', response.status);
      const userData = response.data;
      console.log('[loginWithToken] User data:', userData?.email);
      
      setToken(authToken);
      setUser(userData);
      
      // Store in localStorage
      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      console.log('[loginWithToken] Login successful!');
      return { success: true };
    } catch (error) {
      console.error('[loginWithToken] FAILED:', error.response?.status, error.response?.data || error.message);
      return {
        success: false,
        error: getReadableErrorMessage(error, t, 'notifications.token_login_failed')
      };
    }
  };

  // AUTHORITATIVE credit write: analiz yaniti (server otoriter credits_remaining) -> context + cache,
  //   /auth/me beklemeden ANINDA guncelle (refreshUserData yarisini bypass et). Diger user alanlari korunur.
  const updateCredits = (newCredits) => {
    if (newCredits === undefined || newCredits === null) return;
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, credits_remaining: newCredits };
      try { localStorage.setItem('user', JSON.stringify(updated)); } catch (_) {}
      return updated;
    });
  };

  const value = {
    user,
    setUser,
    token,
    setToken,
    loading,
    register,
    login,
    logout,
    refreshUserData,
    updateCredits,
    loginWithToken,
    isAuthenticated: !!token || !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
